import React from 'react';
import { Link } from 'react-router-dom';
import footerBackground from "../../../assets/footer-background.svg";
import xIcon from "../../../assets/xIcon.svg";
import igIcon from "../../../assets/igIcon.svg";
import telegramIcon from "../../../assets/telegramIcon.svg";
import youtubeIcon from "../../../assets/youtubeIcon.svg";

const LandingFooter: React.FC = () => {
    const currentYear = new Date().getFullYear();
    
    const footerLinks = {
        product: [
            { name: 'Features', href: '/#features' },
            { name: 'Pricing', href: '/pricing' },
            { name: 'API', href: '/docs' },
            { name: 'Security', href: '/security' }
        ],
        company: [
            { name: 'About', href: '/about' },
            { name: 'Blog', href: '/blog' },
            { name: 'Careers', href: '/careers' },
            { name: 'Contact', href: '/contact' }
        ],
        resources: [
            { 
                name: 'Documentation', 
                href: '/documentation/Threat%20Informant%20API%20Reference.html', 
                isExternal: true 
            },
            { name: 'FAQ', href: '/faq' },
            { name: 'Support', href: '/support' },
            { name: 'Status', href: '/status' }
        ],
        legal: [
            { name: 'Privacy Policy', href: '/privacy' },
            { name: 'Terms of Service', href: '/terms' },
            { name: 'Cookie Policy', href: '/cookies' },
            { name: 'GDPR', href: '/gdpr' }
        ]
    };

    const FooterLinkItem = ({ link }: { link: { name: string, href: string, isExternal?: boolean } }) => {
        const className = "text-xs sm:text-sm text-text-secondary-light dark:text-text-secondary-dark hover:text-primary-500 transition-colors duration-200";
        
        if (link.isExternal) {
            return (
                <a href={link.href} target="_blank" rel="noopener noreferrer" className={className}>
                    {link.name}
                </a>
            );
        }
        
        return (
            <Link to={link.href} className={className}>
                {link.name}
            </Link>
        );
    };

    return (
        <footer className="relative overflow-hidden">
            {/* Added pointer-events-none and -z-10 to prevent the image from blocking clicks */}
            <img
                src={footerBackground}
                className="absolute top-0 left-0 w-full h-full object-cover pointer-events-none -z-10"
                alt="Footer background"
            />
            
            {/* Added relative and z-10 to explicitly stack the interactive content on top */}
            <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                {/* Main Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-8">
                    {/* Brand Column */}
                    <div className="lg:col-span-2 space-y-4 sm:space-y-6">
                        <Link to="/" className="flex items-center space-x-2">
                            <div className="flex items-center justify-between px-4 py-4 md:px-8 border-b border-slate-800/30 max-w-7xl mx-auto w-full">
                                <img 
                                    src="/image/logo-2.png" 
                                    alt="ThreatInformant" 
                                    className="h-28 w-64 object-contain"
                                />
                            </div>
                        </Link>
                        <p className="text-sm sm:text-base text-text-secondary-light dark:text-text-secondary-dark max-w-md">
                            Advanced threat intelligence platform focused on dark web monitoring and credential leak detection.
                            Protect your organization from evolving cyber threats.
                        </p>
                        {/* Social Links */}
                        <div className="flex space-x-2 sm:space-x-4">
                            <a
                                href="#"
                                className="p-1.5 sm:p-2 rounded-md text-text-muted-light dark:text-text-muted-dark hover:text-primary-500 hover:bg-primary-500/10 transition-colors duration-200"
                                aria-label="Twitter"
                            >
                                <img src={xIcon} className="w-[1.5rem] h-[1.5rem] sm:w-[2rem] sm:h-[2rem]" alt="Twitter" />
                            </a>
                            <a
                                href="#"
                                className="p-1.5 sm:p-2 rounded-md text-text-muted-light dark:text-text-muted-dark hover:text-primary-500 hover:bg-primary-500/10 transition-colors duration-200"
                                aria-label="Telegram"
                            >
                                <img src={telegramIcon} className="w-[1.5rem] h-[1.5rem] sm:w-[2rem] sm:h-[2rem]" alt="Telegram" />
                            </a>
                            <a
                                href="#"
                                className="p-1.5 sm:p-2 rounded-md text-text-muted-light dark:text-text-muted-dark hover:text-primary-500 hover:bg-primary-500/10 transition-colors duration-200"
                                aria-label="Instagram"
                            >
                                <img src={igIcon} className="w-[1.5rem] h-[1.5rem] sm:w-[2rem] sm:h-[2rem]" alt="Instagram" />
                            </a>
                            <a
                                href="#"
                                className="p-1.5 sm:p-2 rounded-md text-text-muted-light dark:text-text-muted-dark hover:text-primary-500 hover:bg-primary-500/10 transition-colors duration-200"
                                aria-label="YouTube"
                            >
                                <img src={youtubeIcon} className="w-[1.5rem] h-[1.5rem] sm:w-[2rem] sm:h-[2rem]" alt="YouTube" />
                            </a>
                        </div>
                    </div>

                    {/* Product Links */}
                    <div className="space-y-3 sm:space-y-4">
                        <h3 className="text-xs sm:text-sm font-grotesk font-semibold text-text-primary-light dark:text-text-primary-dark uppercase tracking-wider">
                            Product
                        </h3>
                        <ul className="space-y-2">
                            {footerLinks.product.map((link) => (
                                <li key={link.name}>
                                    <FooterLinkItem link={link} />
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Company Links */}
                    <div className="space-y-3 sm:space-y-4">
                        <h3 className="text-xs sm:text-sm font-grotesk font-semibold text-text-primary-light dark:text-text-primary-dark uppercase tracking-wider">
                            Company
                        </h3>
                        <ul className="space-y-2">
                            {footerLinks.company.map((link) => (
                                <li key={link.name}>
                                    <FooterLinkItem link={link} />
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Resources Links */}
                    <div className="space-y-3 sm:space-y-4">
                        <h3 className="text-xs sm:text-sm font-grotesk font-semibold text-text-primary-light dark:text-text-primary-dark uppercase tracking-wider">
                            Resources
                        </h3>
                        <ul className="space-y-2">
                            {footerLinks.resources.map((link) => (
                                <li key={link.name}>
                                    <FooterLinkItem link={link} />
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Legal Links */}
                    <div className="space-y-3 sm:space-y-4">
                        <h3 className="text-xs sm:text-sm font-grotesk font-semibold text-text-primary-light dark:text-text-primary-dark uppercase tracking-wider">
                            Legal
                        </h3>
                        <ul className="space-y-2">
                            {footerLinks.legal.map((link) => (
                                <li key={link.name}>
                                    <FooterLinkItem link={link} />
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div className="mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-border-light dark:border-border-dark-subtle">
                    <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                        <div className="text-xs sm:text-sm text-text-muted-light dark:text-text-muted-dark text-center sm:text-left">
                            © {currentYear} ThreatInformant by Shield Tech Hub. All rights reserved.
                        </div>
                        <div className="text-xs sm:text-sm text-text-muted-light dark:text-text-muted-dark text-center sm:text-left">
                            Built with ❤️ for cybersecurity professionals worldwide
                        </div>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default LandingFooter;